import React from "react";
import { StoreInfoApiResponse } from "../../../../PublicScreens.types";
import FormInput from "../../../FormInput";
import FormField from "../../../FormField";
import { formatAddress } from "../../../../../../../utils/helper";

interface StoreDetailsProps {
  storeInfo: StoreInfoApiResponse;
  storeMode: "view" | "edit";
  setStoreMode: (mode: "view" | "edit") => void;
  setStoreInfo: React.Dispatch<
    React.SetStateAction<StoreInfoApiResponse | null>
  >;
  minutesToTime: (mins: number) => string;
}

const StoreDetails: React.FC<StoreDetailsProps> = ({
  storeInfo,
  storeMode,
  setStoreMode,
  setStoreInfo,
  minutesToTime,
}) => {
  return (
    <div className="rounded-xl border bg-white p-4">
      {/* HEADER */}
      <div className="flex justify-between mb-3 items-center">
        <h3 className="text-sm font-semibold text-gray-500">
          Store Information
        </h3>

        <div className="flex gap-2">
          <button
            onClick={() => setStoreMode("view")}
            className={`px-3 py-2 text-xs rounded-md border ${
              storeMode === "view"
                ? "bg-black text-white"
                : "bg-white text-gray-600"
            }`}
          >
            View
          </button>
          <button
            onClick={() => setStoreMode("edit")}
            className={`px-3 py-2 text-xs rounded-md border ${
              storeMode === "edit"
                ? "bg-black text-white"
                : "bg-white text-gray-600"
            }`}
          >
            Edit
          </button>

          {storeMode === "edit" && (
            <button className="px-3 py-2 text-xs rounded-md border bg-green-500 text-white">
              Save
            </button>
          )}
        </div>
      </div>

      {/* CONTENT */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <FormField label="Store Name">
          <FormInput
            value={storeInfo.storeName}
            readOnly={storeMode === "view"}
            onChange={(val) =>
              setStoreInfo({ ...storeInfo, storeName: val })
            }
          />
        </FormField>

        <FormField label="Social Indicator">
          <FormInput value={storeInfo.socialIndicator} readOnly />
        </FormField>

        <FormField label="Category">
          <FormInput value={storeInfo.category || "—"} readOnly />
        </FormField>

        <FormField label="Sub Category">
          <FormInput value={storeInfo.subCategory || "—"} readOnly />
        </FormField>

        <FormField label="Gender">
          <FormInput value={storeInfo.gender ?? "—"} readOnly />
        </FormField>

        <FormField label="Avg Monthly Footfall">
          <FormInput
            value={storeInfo.avgMonthlyFootfall ?? "—"}
            readOnly
          />
        </FormField>

        <FormField label="Opening Time">
          <FormInput
            value={minutesToTime(storeInfo.openingHourInMinutes)}
            readOnly
          />
        </FormField>

        <FormField label="Closing Time">
          <FormInput
            value={minutesToTime(storeInfo.closingHourInMinutes)}
            readOnly
          />
        </FormField>

        {/* ARRAYS */}
        <FormField label="Income Categories">
          <FormInput
            value={
              storeInfo.incomeCategories?.length
                ? storeInfo.incomeCategories.join(", ")
                : "—"
            }
            readOnly
          />
        </FormField>

        <FormField label="Age Groups">
          <FormInput
            value={
              storeInfo.ageGroups?.length
                ? storeInfo.ageGroups.join(", ")
                : "—"
            }
            readOnly
          />
        </FormField>

        <FormField label="Not Allowed Categories">
          <FormInput
            value={
              storeInfo.notAllowedCategories?.length
                ? storeInfo.notAllowedCategories.join(", ")
                : "—"
            }
            readOnly
          />
        </FormField>

        <FormField label="Not Allowed Subcategories">
          <FormInput
            value={
              storeInfo.notAllowedSubcategories?.length
                ? storeInfo.notAllowedSubcategories.join(", ")
                : "—"
            }
            readOnly
          />
        </FormField>

        {/* ADDRESS */}
        <div className="md:col-span-2">
          <FormField label="Address">
            <textarea
              readOnly
              rows={3}
              value={formatAddress(storeInfo.address) || ""}
              className="w-full rounded-lg border bg-gray-50 px-3 py-2 text-sm font-semibold cursor-not-allowed resize-none"
            />
          </FormField>
        </div>

        <FormField label="Latitude">
          <FormInput value={storeInfo.address.lat ?? "—"} readOnly />
        </FormField>

        <FormField label="Longitude">
          <FormInput value={storeInfo.address.lng ?? "—"} readOnly />
        </FormField>

        {/* SCREENS */}
        <div className="md:col-span-2">
          <FormField label="Screens">
            <textarea
              readOnly
              rows={4}
              value={JSON.stringify(storeInfo.screens, null, 2)}
              className="w-full rounded-lg border bg-gray-50 px-3 py-2 text-sm font-mono cursor-not-allowed resize-none"
            />
          </FormField>
        </div>
      </div>
    </div>
  );
};

export default StoreDetails;
